import { siteConfig } from "@/content/site";
import { trackConversionEvent } from "@/lib/analytics";

const stripeCheckoutHosts = new Set(["buy.stripe.com", "checkout.stripe.com"]);
const purchaseTrackingStoragePrefix = "tssh_purchase_tracked";

const productPayloads = {
  workshop: {
    content_name: "Build a Simple Website for Your Business with AI",
    content_category: "Workshop",
    content_type: "product",
    currency: "USD",
    value: 0,
    workshop_date: siteConfig.workshopDate || "",
  },
  bundle: {
    content_name: "AI Career Advantage Bundle",
    content_category: "Digital Product",
    content_type: "product",
    currency: "USD",
    value: 29,
  },
};

function getUrl(href) {
  try {
    return new URL(href);
  } catch {
    return null;
  }
}

export function isStripeCheckoutHref(href) {
  const url = getUrl(href);

  if (!url) {
    return false;
  }

  return stripeCheckoutHosts.has(url.hostname);
}

function pushDataLayerEvent(eventName, parameters = {}) {
  if (typeof window === "undefined") {
    return false;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...parameters,
  });

  return true;
}

function getPurchaseTrackingStorageKey(product, transactionId) {
  return `${purchaseTrackingStoragePrefix}_${product}_${transactionId}`;
}

function hasTrackedPurchase(product, transactionId) {
  if (typeof localStorage === "undefined") {
    return false;
  }

  try {
    return localStorage.getItem(getPurchaseTrackingStorageKey(product, transactionId)) === "true";
  } catch {
    return false;
  }
}

function markPurchaseTracked(product, transactionId) {
  if (typeof localStorage === "undefined") {
    return;
  }

  try {
    localStorage.setItem(getPurchaseTrackingStorageKey(product, transactionId), "true");
  } catch {
    // If storage is blocked, GA4 can still dedupe with transaction_id.
  }
}

export function getStripeCheckoutSessionId(search = "") {
  try {
    const params = new URLSearchParams(search);

    return (params.get("sid") || params.get("session_id") || "").trim();
  } catch {
    return "";
  }
}

export function trackPricingView({ product = "workshop", location = "pricing" } = {}) {
  const payload = productPayloads[product] || productPayloads.workshop;

  pushDataLayerEvent("view_pricing", {
    ...payload,
    event_id: `${product}_pricing_${Date.now()}`,
    event_location: location,
  });
}

export function trackCheckoutClick({ product = "workshop", label, href, location = "cta" } = {}) {
  if (!isStripeCheckoutHref(href)) {
    return;
  }

  const payload = productPayloads[product] || productPayloads.workshop;
  const parameters = {
    ...payload,
    event_id: `${product}_checkout_${Date.now()}`,
    destination_url: href,
    event_location: location,
    num_items: 1,
    cta_label: label || "Checkout",
  };

  pushDataLayerEvent("begin_checkout", parameters);
  trackConversionEvent("InitiateCheckout", parameters);
}

export function trackPurchase({ product = "workshop", location = "thank_you", transactionId } = {}) {
  const normalizedTransactionId = typeof transactionId === "string" ? transactionId.trim() : "";

  if (normalizedTransactionId && hasTrackedPurchase(product, normalizedTransactionId)) {
    return false;
  }

  const payload = productPayloads[product] || productPayloads.workshop;
  const transactionPayload = normalizedTransactionId
    ? {
        event_id: `${product}_purchase_${normalizedTransactionId}`,
        transaction_id: normalizedTransactionId,
      }
    : {
        event_id: `${product}_purchase_${Date.now()}`,
      };

  const didPush = pushDataLayerEvent("purchase", {
    ...payload,
    ...transactionPayload,
    event_location: location,
    num_items: 1,
    product_name: payload.content_name,
    checkout_provider: "stripe",
  });

  if (didPush && normalizedTransactionId) {
    markPurchaseTracked(product, normalizedTransactionId);
  }

  return didPush;
}

export function trackProductViewContent(product = "workshop") {
  const payload = productPayloads[product] || productPayloads.workshop;

  trackConversionEvent("ViewContent", {
    ...payload,
    event_id: `${product}_view_${Date.now()}`,
    event_location: product === "bundle" ? "bundle_page" : "workshops_page",
  });
}

export function trackWorkshopCheckoutClick(options = {}) {
  trackCheckoutClick({ ...options, product: "workshop" });
}

export function trackWorkshopViewContent() {
  trackProductViewContent("workshop");
}

export function trackBundleCheckoutClick(options = {}) {
  trackCheckoutClick({ ...options, product: "bundle" });
}

export function trackBundleViewContent() {
  trackProductViewContent("bundle");
}
